import { useCallback } from "react";
// src/components/superAdmin/RoleEditor.tsx

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OrganizationRole, OrganizationRoleDto, PolicyPermissionDto } from '../../../../interfaces/superAdmin/SuperAdmin';
import { getRoles, upsertRole, getUsersInRole, assignUserToRole, unassignUserFromRole, AssignedUser, getEmployeesForOrg, getSystemPermissions } from '../../../../services/superAdminServices/SuperAdminPolicyService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { ArrowLeft, Plus, Trash2, Save, Shield, Users, UserPlus, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';

const RoleEditor: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { orgId } = useParams<{ orgId: string }>();
    const numericOrgId = parseInt(orgId || "0");

    const [, setLoading] = useState(false);
    const [roles, setRoles] = useState<OrganizationRole[]>([]);
    const [refData, setRefData] = useState<PolicyPermissionDto[]>([]);
    
    const [editingRole, setEditingRole] = useState<OrganizationRoleDto | null>(null);
    const [activeTab, setActiveTab] = useState<'permissions' | 'users'>('permissions');

    // UI Grouping
    const [expandedModules, setExpandedModules] = useState<string[]>([]);

    // User Assignment State
    const [assignedUsers, setAssignedUsers] = useState<AssignedUser[]>([]);
    const [orgEmployees, setOrgEmployees] = useState<AssignedUser[]>([]); 
    const [selectedUserId, setSelectedUserId] = useState<string>("");

    const loadData = useCallback(async () => {
        setLoading(true);
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        
        const [fetchedRoles, fetchedRef] = await Promise.all([
            getRoles(authToken, numericOrgId),
            getSystemPermissions(authToken)
        ]);

        if (fetchedRoles) setRoles(fetchedRoles);
        if (fetchedRef && fetchedRef.success) setRefData(fetchedRef.data);
        setLoading(false);
    }, [currentUser, numericOrgId]);

useEffect(() => {
        loadData();
    }, [numericOrgId, loadData]);

    // Collapse all modules when switching roles
    useEffect(() => {
        setExpandedModules([]);
    }, [editingRole?.id]);

    // Refresh Users when switching tabs
    const loadAssignedUsers = useCallback(async (roleId: number) => {
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        const users = await getUsersInRole(authToken, numericOrgId, roleId);
        if (users) setAssignedUsers(users);
    }, [currentUser, numericOrgId]);

const editingRoleId = editingRole?.id;
    useEffect(() => {
        if (activeTab === 'users' && editingRoleId) loadAssignedUsers(editingRoleId);
    }, [activeTab, editingRoleId, loadAssignedUsers]);

    useEffect(() => {
        if (activeTab !== 'users' || !editingRoleId || orgEmployees.length > 0 || !currentUser) return;
        const loadEmployees = async () => {
            const authToken = await currentUser.getIdToken();
            const employees = await getEmployeesForOrg(authToken, numericOrgId);
            if (employees) setOrgEmployees(employees);
        };
        void loadEmployees();
    }, [activeTab, editingRoleId, orgEmployees.length, currentUser, numericOrgId]);







    const handleCreateNew = () => {
        setEditingRole({ name: "New Role", description: "", policies: [] });
        setActiveTab('permissions');
        setAssignedUsers([]);
    };

    const handleSelectRole = (role: OrganizationRole) => {
        // Safely map incoming policies to ensure arrays exist
        const mappedPolicies = (role.policies || []).map((p: any) => ({
            policyPermissionId: p.policyPermissionId || refData.find(r => r.permissionKey === p.permissionKey)?.id,
            policyPermissionScopeId: p.policyPermissionScopeId || p.scopeConfig
        })).filter((p: any) => p.policyPermissionId && p.policyPermissionScopeId) as any[];
        
        setEditingRole({
            id: role.id,
            name: role.name,
            description: role.description || "",
            policies: mappedPolicies
        });
        setActiveTab('permissions'); 
    };

    const handleSave = async () => {
        if (!editingRole || !editingRole.name || !currentUser) return;
        setLoading(true);
        const authToken = await currentUser.getIdToken();
        
        // Ensure policies array exists before saving
        const roleToSave = { ...editingRole, policies: editingRole.policies || [] };
        const result = await upsertRole(authToken, numericOrgId, roleToSave);
        
        if (result) {
            await loadData();
            const newRole = (result as OrganizationRole); 
            const refreshedRole = (await getRoles(authToken, numericOrgId) || []).find(r => r.id === newRole.id);
            if(refreshedRole) handleSelectRole(refreshedRole); 
            toast.success("Role saved successfully.");
        }
        setLoading(false);
    };

    // --- USER ASSIGNMENT LOGIC ---

    const handleAssignUser = async () => {
        if (!selectedUserId || !editingRole?.id || !currentUser) return;
        setLoading(true);
        const authToken = await currentUser.getIdToken();
        const success = await assignUserToRole(authToken, numericOrgId, selectedUserId, editingRole.id);
        if (success) {
            await loadAssignedUsers(editingRole.id);
            setSelectedUserId("");
            toast.success("User assigned to role.");
        } else {
            toast.error("Failed to assign user.");
        }
        setLoading(false);
    };

    const handleUnassignUser = async (userId: string) => {
        if (!editingRole?.id || !currentUser) return;
        if (!window.confirm("Remove this user from the role?")) return;
        
        setLoading(true);
        const authToken = await currentUser.getIdToken();
        const success = await unassignUserFromRole(authToken, numericOrgId, userId, editingRole.id);
        if (success) {
            await loadAssignedUsers(editingRole.id);
            toast.success("User removed from role.");
        } else {
            toast.error("Failed to remove user.");
        }
        setLoading(false);
    };

    // --- PERMISSION TOGGLE LOGIC ---

    const toggleExpandModule = (module: string) => {
        setExpandedModules(prev => prev.includes(module) ? prev.filter(m => m !== module) : [...prev, module]);
    };

    const toggleScope = (permissionId: string, scopeId: string) => {
        if (!editingRole) return;

        const currentPolicies = editingRole.policies || [];
        const exists = currentPolicies.some(
            p => p.policyPermissionId === permissionId && p.policyPermissionScopeId === scopeId
        );

        let newPolicies = [...currentPolicies];

        if (exists) {
            newPolicies = newPolicies.filter(
                p => !(p.policyPermissionId === permissionId && p.policyPermissionScopeId === scopeId)
            );
        } else {
            newPolicies.push({
                policyPermissionId: permissionId,
                policyPermissionScopeId: scopeId
            });
        }

        setEditingRole({ ...editingRole, policies: newPolicies });
    };

    // Group Reference Data by Module
    const groupedRefData = refData.reduce((acc, curr) => {
        const moduleName = curr.module || "General";
        if (!acc[moduleName]) acc[moduleName] = [];
        acc[moduleName].push(curr);
        return acc;
    }, {} as Record<string, PolicyPermissionDto[]>);

    const sortedModules = Object.keys(groupedRefData).sort();

    return (
        <div className="w-full h-screen flex flex-col p-4 bg-gray-50 overflow-hidden">
            {/* Header Area */}
            <div className="mb-4 flex items-center justify-between flex-shrink-0">
                <button onClick={() => navigate(-1)} className="flex items-center text-[#7B5CD6] hover:text-[#5e45a3] font-medium text-sm transition-colors">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                </button>
                <h1 className="text-xl font-bold text-gray-800 mb-0">Role & Policy Manager</h1>
            </div>

            {/* Main Content Grid */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-4 min-h-0">
                
                {/* LEFT SIDEBAR: ROLE LIST */}
                <div className="col-span-1 md:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
                    <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-white flex-shrink-0">
                        <h3 className="font-semibold text-gray-800 text-sm tracking-wide mb-0">Roles</h3>
                        <button onClick={handleCreateNew} className="flex items-center text-xs bg-[#7B5CD6] text-white px-3 py-1.5 rounded hover:bg-[#6a4bc0] transition-colors shadow-sm">
                            <Plus className="w-3.5 h-3.5 mr-1.5" /> New
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-white">
                        {roles.map(role => (
                            <div 
                                key={role.id} 
                                onClick={() => handleSelectRole(role)} 
                                className={`p-3 rounded-md cursor-pointer transition-all border-l-4 ${editingRole?.id === role.id ? 'bg-purple-50 border-[#7B5CD6]' : 'border-transparent hover:bg-gray-50'}`}
                            >
                                <div className="font-semibold text-gray-900 text-sm">{role.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* RIGHT PANEL: EDITOR */}
                <div className="col-span-1 md:col-span-9 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col h-full overflow-hidden">
                    {editingRole ? (
                        <>
                            {/* Toolbar */}
                            <div className="p-6 border-b border-gray-200 bg-white flex-shrink-0">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex-1 mr-4 space-y-3">
                                        <div>
                                            <input 
                                                className="w-full text-lg font-bold bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-gray-900 placeholder-gray-400 transition-all shadow-sm" 
                                                value={editingRole.name}
                                                onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                                                placeholder="Role Name"
                                            />
                                        </div>
                                        <div>
                                            <input 
                                                className="w-full text-sm bg-white border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] text-gray-700 placeholder-gray-400 transition-all shadow-sm" 
                                                value={editingRole.description || ""}
                                                onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                                                placeholder="Add a description..."
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={handleSave} className="flex items-center px-6 py-2 bg-[#7B5CD6] text-white rounded text-sm font-medium hover:bg-[#6a4bc0] transition-colors shadow-sm">
                                            <Save className="w-4 h-4 mr-2"/> Save Changes
                                        </button>
                                    </div>
                                </div>
                                <div className="flex space-x-6 border-b border-gray-200 text-sm font-medium pt-2">
                                    <button onClick={() => setActiveTab('permissions')} className={`pb-2 transition-colors ${activeTab === 'permissions' ? 'border-b-2 border-[#7B5CD6] text-[#7B5CD6]' : 'text-gray-500 hover:text-gray-700'}`}>
                                        <div className="flex items-center"><Shield className="w-4 h-4 mr-2" /> Permissions</div>
                                    </button>
                                    <button onClick={() => setActiveTab('users')} className={`pb-2 transition-colors ${activeTab === 'users' ? 'border-b-2 border-[#7B5CD6] text-[#7B5CD6]' : 'text-gray-500 hover:text-gray-700'}`}>
                                        <div className="flex items-center"><Users className="w-4 h-4 mr-2" /> Assigned Users</div>
                                    </button>
                                </div>
                            </div>

                            {/* CONTENT AREA */}
                            <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                                {activeTab === 'permissions' && (
                                    <div className="space-y-4">
                                        <div className="mb-4 px-1">
                                            <h3 className="text-sm font-bold text-gray-800 mb-0">Permission Configuration</h3>
                                        </div>
                                        
                                        {sortedModules.map(moduleName => {
                                            const modulePermissions = groupedRefData[moduleName];
                                            const isExpanded = expandedModules.includes(moduleName);
                                            
                                            const currentPolicies = editingRole.policies || [];
                                            const activeModuleCount = currentPolicies.filter(
                                                p => modulePermissions.some(mp => mp.id === p.policyPermissionId)
                                            ).length;

                                            return (
                                                <div key={moduleName} className={`bg-white border rounded-lg shadow-sm transition-all ${isExpanded ? 'border-[#7B5CD6]/40' : 'border-gray-200 hover:border-gray-300'}`}>
                                                    <div 
                                                        onClick={() => toggleExpandModule(moduleName)}
                                                        className={`px-4 py-3 flex justify-between items-center cursor-pointer select-none transition-colors rounded-t-lg ${isExpanded ? 'bg-purple-50/50 border-b border-gray-100' : 'bg-white rounded-b-lg'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <div className={`p-1 rounded-md transition-colors ${isExpanded ? 'bg-purple-100 text-[#7B5CD6]' : 'bg-gray-100 text-gray-400'}`}>
                                                                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                            </div>
                                                            <div>
                                                                <h4 className="font-bold text-gray-800 text-sm mb-0">{moduleName}</h4>
                                                            </div>
                                                        </div>
                                                        
                                                        {activeModuleCount > 0 && (
                                                            <span className="flex items-center gap-1.5 bg-purple-50 text-[#7B5CD6] text-xs px-2 py-0.5 rounded border border-purple-200 font-bold">
                                                                <CheckCircle2 className="w-3.5 h-3.5" />
                                                                {activeModuleCount} Active Rules
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* CSS Grid technique for smooth height animation */}
                                                    <div 
                                                        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                                                            isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                                                        }`}
                                                    >
                                                        <div className="overflow-hidden">
                                                            <div className="p-4 bg-white space-y-6 rounded-b-lg">
                                                                {modulePermissions.map(permDef => (
                                                                    <div key={permDef.id} className="pb-4 last:pb-0 border-b last:border-0 border-gray-100">
                                                                        <div className="mb-3">
                                                                            <h5 className="font-semibold text-gray-700 text-xs font-mono bg-gray-50 inline-block px-2 py-1 rounded border border-gray-200 mb-0">
                                                                                {permDef.permissionKey}
                                                                            </h5>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                                                                            {permDef.policyPermissionScopes.map(scope => {
                                                                                const isChecked = currentPolicies.some(
                                                                                    p => p.policyPermissionId === permDef.id && p.policyPermissionScopeId === scope.id
                                                                                );

                                                                                return (
                                                                                    <label 
                                                                                        key={scope.id} 
                                                                                        title={scope.scope}
                                                                                        className={`flex items-start p-3 rounded border cursor-pointer transition-all ${isChecked ? 'bg-purple-50 border-[#7B5CD6] shadow-sm' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                                                                                    >
                                                                                        <div className="flex items-center h-5">
                                                                                            <input 
                                                                                                type="checkbox"
                                                                                                className="w-4 h-4 rounded border-gray-300 text-[#7B5CD6] focus:ring-[#7B5CD6] transition-colors"
                                                                                                checked={isChecked}
                                                                                                onChange={() => toggleScope(permDef.id, scope.id)}
                                                                                            />
                                                                                        </div>
                                                                                        <span className={`ml-3 text-sm flex-1 break-words ${isChecked ? 'font-bold text-[#7B5CD6]' : 'text-gray-600'}`}>
                                                                                            {scope.scope}
                                                                                        </span>
                                                                                    </label>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                {activeTab === 'users' && (
                                    <div className="space-y-6">
                                        <div className="flex gap-4 items-end bg-purple-50 p-4 rounded-lg border border-purple-100">
                                            <div className="flex-1">
                                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Add User to Role</label>
                                                <select 
                                                    className="w-full text-sm border-gray-300 rounded focus:ring-1 focus:ring-[#7B5CD6] focus:border-[#7B5CD6] shadow-sm"
                                                    value={selectedUserId}
                                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                                >
                                                    <option value="">Select an Employee...</option>
                                                    {orgEmployees.map(emp => (
                                                        <option key={emp.id} value={emp.id}>
                                                            {emp.firstName} {emp.lastName} ({emp.email})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button 
                                                onClick={handleAssignUser}
                                                disabled={!selectedUserId}
                                                className="flex items-center px-4 py-2 bg-[#7B5CD6] text-white rounded text-sm font-medium hover:bg-[#6a4bc0] disabled:opacity-50 transition-colors shadow-sm"
                                            >
                                                <UserPlus className="w-4 h-4 mr-2"/> Assign
                                            </button>
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-700 mb-3">Assigned Users ({assignedUsers.length})</h3>
                                            {assignedUsers.length === 0 ? (
                                                <div className="text-gray-400 text-sm italic">No users assigned to this role yet.</div>
                                            ) : (
                                                <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                                    <table className="min-w-full divide-y divide-gray-200">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Name</th>
                                                                <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Legacy Role</th>
                                                                <th className="px-4 py-3 text-right"></th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                            {assignedUsers.map(u => (
                                                                <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                                        <div className="font-semibold">{u.firstName} {u.lastName}</div>
                                                                        <div className="text-gray-500 text-xs">{u.email}</div>
                                                                    </td>
                                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                                        {u.legacyRoles.join(", ")}
                                                                    </td>
                                                                    <td className="px-4 py-3 text-right">
                                                                        <button 
                                                                            onClick={() => handleUnassignUser(u.id)}
                                                                            className="text-gray-400 hover:text-red-500 transition-colors p-1"
                                                                            title="Remove role"
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50">
                            <Shield className="w-16 h-16 mb-4 text-gray-300" strokeWidth={1.5} />
                            <p className="text-gray-500 font-medium">Select a role to configure permissions.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoleEditor;